const UINT32 = 2 ** 32; // Range of a 32-bit unsigned integer
const INT32_SIGN_BIT = 2 ** 31; // The bit that flips a 32-bit number from positive to negative
const HEX_COLOR_SPACE = 16 ** 6; // Total number of possible 6-digit hex colors (#000000 to #ffffff)

/**
 * Clamps a number into the signed 32-bit integer range [-2^31, 2^31 - 1].
 * Without this, the hash would overflow into floating-point territory and
 * produce different results than the original bitwise implementation.
 */
const toInt32 = (n: number): number => {
  const unsigned = ((n % UINT32) + UINT32) % UINT32;
  return unsigned >= INT32_SIGN_BIT ? unsigned - UINT32 : unsigned;
};

/**
 * Hashes a string into an unsigned 32-bit integer using the djb2 algorithm,
 * clamping to a signed 32-bit integer at each step to avoid floating-point drift.
 */
const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = toInt32(str.charCodeAt(i) + (hash * 32 - hash));
  }
  return ((hash % UINT32) + UINT32) % UINT32;
};

const clampChannel = (n: number): number => Math.min(255, Math.max(0, n));

const parseHexColor = (hex: string): { r: number; g: number; b: number } => {
  const clean = hex.replace('#', '');
  return {
    r: parseInt(clean.slice(0, 2), 16),
    g: parseInt(clean.slice(2, 4), 16),
    b: parseInt(clean.slice(4, 6), 16),
  };
};

const toHexChannel = (n: number): string => n.toString(16).padStart(2, '0');

/**
 * Converts a string into a deterministic hex color code.
 *
 * With no extra arguments, the color is derived from the full hex color space.
 *
 * When `shade` and `range` are provided, the result is instead a variation of `shade`:
 * the string's hash is mapped to an offset within [-range, range] and applied uniformly
 * to each RGB channel, producing a shade (e.g. lighter/darker) of the given color rather
 * than an arbitrary one. Larger `range` values allow wider swings from the base shade.
 */
export const getHexFromString = (str: string, shade?: string, range?: number): string => {
  const unsigned = hashString(str);

  if (typeof shade !== 'string' || typeof range !== 'number') {
    const colorValue = unsigned % HEX_COLOR_SPACE;
    return `#${colorValue.toString(16).padStart(6, '0')}`;
  }

  const span = range * 2 + 1;
  const delta = (unsigned % span) - range;
  const { r, g, b } = parseHexColor(shade);
  return `#${toHexChannel(clampChannel(r + delta))}${toHexChannel(clampChannel(g + delta))}${toHexChannel(
    clampChannel(b + delta),
  )}`;
};
